import { useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import type { RootState } from '@/app/store';
import { selectResumeSubscriptionPauseLoading } from '@/features/billing/billingSlice';
import {
  fetchSubscription,
  fetchUsage,
  resumeSubscriptionPause,
} from '@/features/billing/billingThunks';
import { fetchWorkspaceById } from '@/features/workspace/workspaceThunks';
import { toast } from 'react-toastify';
import { Button } from '../ui/button';
import Loader from '../ui/Loader';
import { useTranslation } from 'react-i18next';

function formatPauseEndsAt(dateRaw: string | null | undefined, lang: string): string | null {
  if (!dateRaw) return null;
  const date = new Date(dateRaw);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(lang, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function SuspendModal() {
  const { t, i18n } = useTranslation('billing');
  const dispatch = useAppDispatch();
  const workspace = useAppSelector((s: RootState) => s.workspace.currentWorkspace);
  const workspaceId = workspace?.id;
  const isResuming = useAppSelector(selectResumeSubscriptionPauseLoading);
  const isSuspended = workspace?.productAccessStatus === 'SUSPENDED';

  const handleResumePause = useCallback(async () => {
    if (!workspaceId || isResuming) return;

    try {
      await dispatch(resumeSubscriptionPause(workspaceId)).unwrap();
      await Promise.all([
        dispatch(fetchSubscription(workspaceId)).unwrap(),
        dispatch(fetchUsage(workspaceId)).unwrap(),
        dispatch(fetchWorkspaceById(workspaceId)).unwrap(),
      ]);
      toast.success(t('suspend.toast.success'));
    } catch (e: unknown) {
      const err = e as { message?: string };
      toast.error(err?.message ?? t('suspend.toast.error'));
    }
  }, [dispatch, workspaceId, isResuming]);

  if (!isSuspended) return null;

  const pauseEndsAtFormatted = formatPauseEndsAt(workspace.productAccessPauseEndsAt, i18n.language);

  return (
    <Dialog open onOpenChange={() => {}}>
      <DialogContent
        hideCloseButton
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>{t('suspend.title')}</DialogTitle>
          <DialogDescription className="text-base text-secondary">
            {pauseEndsAtFormatted
              ? t('suspend.suspendedUntil', { date: pauseEndsAtFormatted })
              : t('suspend.suspended')}
          </DialogDescription>
          <Button
            className="mt-4"
            disabled={!workspaceId || isResuming}
            onClick={handleResumePause}
          >
            {isResuming && <Loader />}
            {t('suspend.reactivate')}
          </Button>
        </DialogHeader>
      </DialogContent>
    </Dialog>
  );
}
